import { useMemo } from 'react';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { SelectedAgentForInputHat } from '@atlassian/conversation-assistant-chat-prompt-input/ui/chat-prompt-input/types';
import {
	DEFAULT_AGENT_UUID,
	isDefaultRovoChat,
} from '@atlassian/conversation-assistant-utils/default-agent';

import { DEFAULT_AGENT } from '../../controllers/agents/constants';
import { useConversation } from '../../controllers/conversation/useConversation';

import { getInputHatSelectedAgent } from './getInputHatSelectedAgent';

/**
 * Resolves the selected-agent input hat from the immediate picker payload and
 * the conversation baseline. Specialist agents require picker-provided identity
 * metadata; default Rovo is the local no-identity exception.
 */
export const useInputHatSelectedAgent = ({
	conversationId,
	activeSelectedAgentId,
	selectedAgentIdentityAccountId,
}: {
	conversationId: string;
	activeSelectedAgentId: string | undefined;
	selectedAgentIdentityAccountId?: string;
}): SelectedAgentForInputHat | undefined => {
	const isAgentSelectionEnabled = isExperimentEnabled('rovo_chat_mention_agents');

	// The default Rovo agent is a client-side sentinel with no backend record, so hydrating it via
	// the agents API would 404. Use the local DEFAULT_AGENT constant for it instead.
	const isDefaultAgentSelected =
		isAgentSelectionEnabled && isDefaultRovoChat({ agentId: activeSelectedAgentId });
	const selectedAgentCandidateForInputHat = isDefaultAgentSelected
		? { ...DEFAULT_AGENT, id: DEFAULT_AGENT_UUID, name: 'Rovo' }
		: activeSelectedAgentId && selectedAgentIdentityAccountId
			? {
					id: activeSelectedAgentId,
					name: '',
					identity_account_id: selectedAgentIdentityAccountId,
				}
			: undefined;
	const { selectedAgent: currentAgent } = useConversation({ conversationId });

	const inputHatSelectedAgent = isAgentSelectionEnabled
		? getInputHatSelectedAgent(
				selectedAgentCandidateForInputHat,
				activeSelectedAgentId,
				currentAgent,
			)
		: undefined;

	const id = inputHatSelectedAgent?.id;
	const resolvedName = inputHatSelectedAgent?.name;
	const resolvedIdentityAccountId = inputHatSelectedAgent?.identity_account_id ?? undefined;
	const isDefault = isDefaultAgentSelected || undefined;

	return useMemo(
		() =>
			id && (isDefault || resolvedIdentityAccountId)
				? {
						id,
						name: resolvedName,
						identityAccountId: resolvedIdentityAccountId,
						isDefault,
					}
				: undefined,
		[id, resolvedName, resolvedIdentityAccountId, isDefault],
	);
};
